/**
 * Authentication Hook
 * Custom hook for managing authentication state and operations
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated
  const checkAuthStatus = useCallback(() => {
    try {
      const sessionData = authService.getCurrentSession();
      if (sessionData && sessionData.isAuthenticated) {
        setUser(sessionData.user);
        setIsAuthenticated(true);
        return true;
      }
    } catch (error) {
      // Auth check failed silently
    }

    setUser(null);
    setIsAuthenticated(false);
    return false;
  }, []);

  // Login function
  const login = useCallback(async (credentials) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      if (response.success) {
        const { user: userData } = response.data;

        // Update local state (session is already set by authService)
        setUser(userData);
        setIsAuthenticated(true);

        // Navigate based on role - use backend role mapping
        const userRole = userData.user_role || userData.role;
        const dashboardPath = userRole === 'Superuser' || userRole === 'Staff'
          ? '/admin-dashboard'
          : '/user-dashboard';
        navigate(dashboardPath);

        return { success: true, user: userData };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: 'Login failed. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const sessionData = authService.getCurrentSession();
      if (sessionData?.refresh) {
        await authService.logout(sessionData.refresh);
      }
    } catch (error) {
      // Logout error handled silently
    } finally {
      authService.clearSession();
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  // Register function
  const register = useCallback(async (userData) => {
    setIsLoading(true);
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      return { success: false, error: 'Registration failed. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    const userRole = user?.user_role || user?.role;
    return userRole === role;
  }, [user]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    const userRole = user?.user_role || user?.role;
    return userRole === 'Superuser' || userRole === 'Staff';
  }, [user]);

  // Check if user can login (based on API response)
  const canLogin = useCallback(() => {
    if (!user) return false;

    // Use authService method for consistent logic
    return authService.canUserLogin(user);
  }, [user]);

  // Get user status message
  const getUserStatusMessage = useCallback(() => {
    if (!user) return 'User not found';
    return authService.getUserStatusMessage(user);
  }, [user]);

  // Clear session and redirect to login
  const clearSession = useCallback(() => {
    authService.clearSession();
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');
  }, [navigate]);

  // Initialize auth state
  useEffect(() => {
    checkAuthStatus();
    setIsLoading(false);
  }, [checkAuthStatus]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    hasRole,
    isAdmin,
    canLogin,
    getUserStatusMessage,
    checkAuthStatus,
    clearSession,
  };
};
